/**
 * Students Management Page
 *
 * Main page for managing student roster.
 * Allows Owner/Manager/Admin to view and manage students.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Alert,
  CircularProgress,
} from '@mui/material';
import Header from '../../components/layout/Header';
import { YouTubeVideoButton } from '../../components/common';
import { useOwnerManagerAdminAccess } from '../../hooks/useToolAccess';
import { useSessionAuth } from '../../hooks/useSessionAuth';
import StudioSelectorControl from '../../components/common/StudioSelectorControl';
import StudentsTable from './StudentsTable';
import NoToolAccess from '../../components/common/NoToolAccess';

const StudentsManagement: React.FC = () => {
  const navigate = useNavigate();

  // Get authentication state
  const { isAuthenticated, isLoading: authLoading } = useSessionAuth();

  // Get studio access information (Owner/Manager/Admin)
  const {
    eligibleStudios,
    hasToolAccess,
    hasSingleStudio,
    isLoading: toolAccessLoading,
  } = useOwnerManagerAdminAccess();

  // Component state
  const [selectedStudioId, setSelectedStudioId] = useState<string | null>(null);

  // Handle studio selection and access control
  useEffect(() => {
    // Wait for authentication and tool access data to load
    if (authLoading || toolAccessLoading) {
      return;
    }

    // If user is not authenticated, redirect to login
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    // Auto-select if user has exactly one eligible studio
    if (hasSingleStudio) {
      const [studio] = eligibleStudios;
      setSelectedStudioId(studio.id);
    }
  }, [
    navigate,
    authLoading,
    isAuthenticated,
    toolAccessLoading,
    hasSingleStudio,
    eligibleStudios,
  ]);

  // Show no access message if user doesn't have required permissions
  if (!authLoading && !toolAccessLoading && !hasToolAccess) {
    return (
      <NoToolAccess
        toolName="Students"
        requiredRoles={['owner', 'manager', 'admin']}
      />
    );
  }

  // Show loading while checking access
  if (authLoading || toolAccessLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Header />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {/* Page Header */}
        <Box mb={4}>
          <Typography variant="h4" component="h1" gutterBottom>
            Student Roster
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your student roster with Excel import/export capabilities
          </Typography>
        </Box>

        {/* Tutorial Video Section */}
        <Box
          sx={{
            mb: 4,
            p: 2,
            backgroundColor: 'action.hover',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
            Watch our step-by-step tutorial to learn how to import and manage
            your student roster with Excel files.
          </Typography>
          <YouTubeVideoButton
            videoId="8NGyiIKLADQ"
            videoTitle="Students Tool Tutorial"
            buttonText="Watch Tutorial"
            variant="outlined"
            size="small"
          />
        </Box>

        {/* Studio Selector - Only shows for multiple studios */}
        <Box mb={4}>
          <StudioSelectorControl
            studios={eligibleStudios}
            selectedStudioId={selectedStudioId}
            onStudioChange={setSelectedStudioId}
          />
        </Box>

        {/* Info Alert for Multiple Studios */}
        {eligibleStudios.length > 1 && !selectedStudioId && (
          <Box mb={4}>
            <Alert severity="info" sx={{ maxWidth: 400 }}>
              Please select a studio to manage its student roster.
            </Alert>
          </Box>
        )}

        {/* Content area - only show if studio is selected */}
        {selectedStudioId && (
          <Paper sx={{ p: 3 }}>
            <StudentsTable studioId={selectedStudioId} />
          </Paper>
        )}
      </Container>
    </>
  );
};

export default StudentsManagement;
